package rendering

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.focusable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.key.Key
import androidx.compose.ui.input.key.KeyEventType
import androidx.compose.ui.input.key.key
import androidx.compose.ui.input.key.onKeyEvent
import androidx.compose.ui.input.key.type
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import engine.model.GameSnapshot
import engine.model.MatchConfig
import engine.model.GameGoal

// ── Color palette ─────────────────────────────────────────────────────────────

private val COLOR_OVERLAY_BG = Color(0xCC000000)
private val COLOR_MODAL_BG = Color(0xFF0D0D1A)
private val COLOR_MODAL_BORDER = Color(0xFF333355)
private val COLOR_GOAL_MET = Color(0xFF00E676)
private val COLOR_GAME_OVER = Color(0xFFFF1744)
private val COLOR_BUTTON_HIGHLIGHT = Color(0xFF00E5FF)
private val COLOR_STAT_LABEL = Color(0xFF888888)
private val COLOR_STAT_VALUE = Color(0xFFFFFFFF)

// ── Text styles ───────────────────────────────────────────────────────────────

@Composable
private fun monoStyle() = TextStyle(
    fontFamily = fontMono(),
    letterSpacing = 0.05.sp,
)

// ── GameOverlay ───────────────────────────────────────────────────────────────

/**
 * Full-screen overlay shown when the game ends (game over) or when the goal
 * is met (e.g. Sprint 40 lines cleared).
 *
 * Wraps everything in [AnimatedVisibility] with [fadeIn]/[fadeOut] so the
 * overlay fades in and out smoothly.
 *
 * The modal captures keyboard focus when [visible] is `true` and handles:
 * - **Enter / R** → [onRetry]
 * - **Escape / M** → [onMenu]
 *
 * @param visible     Whether the overlay is currently shown.
 * @param isGoalMet   `true` when the player met the objective; `false` for game over.
 * @param snap        Latest [GameSnapshot] used to display end-of-game stats.
 *                    May be `null` if the snapshot is unavailable.
 * @param modeConfig  The [MatchConfig] for the current session, used to label
 *                    the goal type in the stats row.
 * @param onRetry     Called when the player chooses to retry.
 * @param onMenu      Called when the player chooses to return to the menu.
 * @param modifier    Applied to the outermost [Box] container.
 */
@Composable
fun GameOverlay(
    visible: Boolean,
    isGoalMet: Boolean,
    snap: GameSnapshot?,
    modeConfig: MatchConfig?,
    onRetry: () -> Unit,
    onMenu: () -> Unit,
    modifier: Modifier = Modifier,
) {
    AnimatedVisibility(
        visible = visible,
        enter = fadeIn(),
        exit = fadeOut(),
    ) {
        val focusRequester = remember { FocusRequester() }

        // Track which button is focused: 0 = RETRY, 1 = MENU
        var focusedButton by remember { mutableStateOf(0) }

        // Request focus when the overlay becomes visible so key events are captured
        LaunchedEffect(Unit) {
            kotlinx.coroutines.delay(50)
            focusRequester.requestFocus()
        }

        // Reset focused button to RETRY each time overlay becomes visible
        LaunchedEffect(visible) {
            if (visible) focusedButton = 0
        }

        // Semi-transparent dark backdrop covering the full area
        Box(
            modifier = modifier
                .fillMaxSize()
                .background(COLOR_OVERLAY_BG)
                .focusRequester(focusRequester)
                .focusable()
                .onKeyEvent { event ->
                    if (event.type != KeyEventType.KeyDown) return@onKeyEvent false
                    when (event.key) {
                        Key.Enter -> { onRetry(); true }
                        Key.R -> { onRetry(); true }
                        Key.Escape -> { onMenu(); true }
                        Key.M -> { onMenu(); true }
                        Key.DirectionLeft, Key.DirectionRight,
                        Key.Tab -> {
                            focusedButton = if (focusedButton == 0) 1 else 0
                            true
                        }
                        else -> false
                    }
                },
            contentAlignment = Alignment.Center,
        ) {
            // Centered modal box
            Column(
                modifier = Modifier
                    .widthIn(min = 280.dp, max = 400.dp)
                    .background(COLOR_MODAL_BG)
                    .border(width = 1.dp, color = COLOR_MODAL_BORDER)
                    .padding(horizontal = 32.dp, vertical = 28.dp),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.spacedBy(0.dp),
            ) {
                // ── Title ─────────────────────────────────────────────────────
                val titleText = if (isGoalMet) "GOAL MET" else "GAME OVER"
                val titleColor = if (isGoalMet) COLOR_GOAL_MET else COLOR_GAME_OVER

                Text(
                    text = titleText,
                    style = monoStyle().copy(
                        fontSize = 28.sp,
                        color = titleColor,
                        letterSpacing = 0.15.sp,
                    ),
                    textAlign = TextAlign.Center,
                )

                Spacer(modifier = Modifier.height(20.dp))

                // ── Stats ─────────────────────────────────────────────────────
                if (snap != null) {
                    val hud = snap.hudInfo

                    // Score — from GravitarGame.points (not in snapshot directly);
                    // use sessionTimeSeconds as a proxy for time, and hudInfo for lines/level.
                    // Score is tracked by ScoreTracker and exposed via GravitarGame.points,
                    // but GameSnapshot does not carry it. We show the stats that are available.

                    hud.totalLinesCleared?.let { lines ->
                        StatRow(label = "LINES", value = lines.toString())
                    }

                    hud.currentLevel?.let { level ->
                        StatRow(label = "LEVEL", value = level.toString())
                    }

                    hud.sessionTimeSeconds?.let { secs ->
                        StatRow(label = "TIME", value = formatTime(secs))
                    }

                    // Show goal progress if a goal type is configured
                    val goalType = modeConfig?.objective?.goalType
                    val goalValue = modeConfig?.objective?.goalValue
                    if (goalType != null && goalType != GameGoal.NONE && goalValue != null) {
                        val goalLabel = when (goalType) {
                            GameGoal.LINES -> "GOAL"
                            GameGoal.TIME  -> "TARGET"
                            GameGoal.NONE  -> null
                        }
                        if (goalLabel != null) {
                            val goalDisplay = when (goalType) {
                                GameGoal.LINES -> goalValue.toInt().toString()
                                GameGoal.TIME  -> formatTime(goalValue)
                                GameGoal.NONE  -> ""
                            }
                            StatRow(label = goalLabel, value = goalDisplay)
                        }
                    }

                    Spacer(modifier = Modifier.height(4.dp))
                }

                Spacer(modifier = Modifier.height(20.dp))

                // ── Buttons ───────────────────────────────────────────────────
                Row(
                    horizontalArrangement = Arrangement.spacedBy(16.dp),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    OverlayButton(
                        label = "RETRY",
                        hint = "[Enter / R]",
                        highlighted = focusedButton == 0,
                        onClick = onRetry,
                    )
                    OverlayButton(
                        label = "MENU",
                        hint = "[Esc / M]",
                        highlighted = focusedButton == 1,
                        onClick = onMenu,
                    )
                }

                Spacer(modifier = Modifier.height(8.dp))

                // Keyboard hint
                Text(
                    text = "← → or Tab to switch",
                    style = monoStyle().copy(
                        fontSize = 8.sp,
                        color = COLOR_STAT_LABEL,
                    ),
                    textAlign = TextAlign.Center,
                )
            }
        }
    }
}

// ── StatRow ───────────────────────────────────────────────────────────────────

/**
 * A single stat row with a muted label on the left and a bright value on the right.
 */
@Composable
private fun StatRow(
    label: String,
    value: String,
    modifier: Modifier = Modifier,
) {
    Row(
        modifier = modifier
            .fillMaxWidth()
            .padding(vertical = 3.dp),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Text(
            text = label,
            style = monoStyle().copy(
                fontSize = 10.sp,
                color = COLOR_STAT_LABEL,
                letterSpacing = 0.1.sp,
            ),
        )
        Text(
            text = value,
            style = monoStyle().copy(
                fontSize = 14.sp,
                color = COLOR_STAT_VALUE,
            ),
        )
    }
}

// ── OverlayButton ─────────────────────────────────────────────────────────────

/**
 * An outlined button used in the game overlay.
 *
 * When [highlighted] is `true` the border and label use the cyan accent colour,
 * indicating keyboard focus. Clicking the button calls [onClick].
 *
 * @param label       Primary button label (e.g. "RETRY").
 * @param hint        Small keyboard shortcut hint shown below the label.
 * @param highlighted Whether this button is currently keyboard-focused.
 * @param onClick     Called when the button is activated.
 */
@Composable
private fun OverlayButton(
    label: String,
    hint: String,
    highlighted: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val borderColor = if (highlighted) COLOR_BUTTON_HIGHLIGHT else COLOR_MODAL_BORDER
    val labelColor = if (highlighted) COLOR_BUTTON_HIGHLIGHT else COLOR_STAT_VALUE

    Box(
        modifier = modifier
            .width(100.dp)
            .clickable { onClick() }
            .border(width = 1.dp, color = borderColor)
            .padding(horizontal = 12.dp, vertical = 10.dp),
        contentAlignment = Alignment.Center,
    ) {
        Column(horizontalAlignment = Alignment.CenterHorizontally) {
            Text(
                text = label,
                style = monoStyle().copy(
                    fontSize = 13.sp,
                    color = labelColor,
                    letterSpacing = 0.1.sp,
                ),
                textAlign = TextAlign.Center,
            )
            Spacer(modifier = Modifier.height(2.dp))
            Text(
                text = hint,
                style = monoStyle().copy(
                    fontSize = 8.sp,
                    color = COLOR_STAT_LABEL,
                ),
                textAlign = TextAlign.Center,
            )
        }
    }
}

// ── Time formatting ───────────────────────────────────────────────────────────

/**
 * Formats a time value in seconds as "MM:SS".
 *
 * @param seconds Time in seconds (may be fractional).
 * @return Formatted string, e.g. "01:23".
 */
private fun formatTime(seconds: Double): String {
    val totalSecs = seconds.toLong().coerceAtLeast(0L)
    val mins = totalSecs / 60L
    val secs = totalSecs % 60L
    return "${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}"
}
